import { getWebInstrumentations, initializeFaro } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { getAppName } from '../app-name/app-name';
import { getGlobalProperties } from '../global-properties/global-properties';
import { v4 } from 'uuid';
import isElectron from 'is-electron';
import { electronBridge } from '../electron-bridge/electron-bridge';
import { normalizeUrl } from './utils';
import { getMetricTags, getXHRMethod } from '@m43/core';

export async function setupFaro() {
    const globalProperties = await getGlobalProperties();
    const url = globalProperties.FARO_URL;
    const apiKey = globalProperties.FARO_API_KEY;
    const shouldNoop = !url || !apiKey;
    // for dev, because of the default cors handling, the `traceparent` header wont
    // be sent unless we customize this option
    const xhrInstrumentation =
        process.env.NODE_ENV === 'production'
            ? {}
            : {
                  propagateTraceHeaderCorsUrls: [/.*/],
              };
    initializeFaro({
        url,
        apiKey,
        // we always want to initialize faro whether or not we have a url and key to prevent runtime errors
        // in usages of faro, however if neither of these is provided we start it paused so that it doesn't
        // actually do anything
        paused: shouldNoop,
        transports: shouldNoop ? [] : undefined,
        beforeSend(obj) {
            const url = (obj.payload as any).attributes?.['http.url'];
            if (url && (url.includes('logrocket') || url.includes('sentry'))) {
                return null;
            }
            return obj;
        },
        app: {
            name: getAppName() === 'cad' ? 'cad-fe' : 'fr-fe',
            namespace: 'cad',
            version: globalProperties.VERSION,
            environment: globalProperties.ENVIRONMENT,
        },
        sessionTracking: {
            enabled: true,
            generateSessionId() {
                // all windows should share a session id in electron
                return isElectron() ? electronBridge?.args['--client-session-id'] ?? v4() : v4();
            },
        },
        instrumentations: [
            ...getWebInstrumentations({
                captureConsole: false,
                enablePerformanceInstrumentation: true,
            }),
            new TracingInstrumentation({
                instrumentationOptions: {
                    ...xhrInstrumentation,
                    xhrInstrumentationOptions: {
                        applyCustomAttributesOnSpan: (span, xhr) => {
                            try {
                                span.setAttributes({
                                    ...getMetricTags(),
                                    environment: globalProperties.ENVIRONMENT ?? '',
                                });
                                const method = getXHRMethod(xhr);
                                const url = xhr.responseURL || '';
                                const path = new URL(url, window.location.href).pathname;
                                span.updateName(`${method} ${normalizeUrl(path)}`);
                            } catch (e) {
                                // no-op
                            }
                        },
                    },
                    fetchInstrumentationOptions: {
                        applyCustomAttributesOnSpan: (span, request) => {
                            if (!(request instanceof Request)) {
                                return;
                            }
                            try {
                                span.setAttributes({
                                    ...getMetricTags(),
                                    environment: globalProperties.ENVIRONMENT ?? '',
                                });
                                const { method, url } = request;
                                const path = new URL(url, window.location.href).pathname;
                                span.updateName(`${method} ${normalizeUrl(path)}`);
                            } catch (e) {
                                // no-op
                            }
                        },
                    },
                },
            }),
        ],
    });
}
